import type { AppliedMove, PrincipleSignal } from "@chessla/shared";
import { principleSignalsForVerifiedMove } from "@/features/analysis";
import type { TemporaryMoveGrade } from "@/features/board/temporary-play";

export interface TemporaryMoveEvidence {
  destination: string;
  motifs: string[];
  principleSignals: PrincipleSignal[];
}

const qualityMotif = (grade: TemporaryMoveGrade): string | null => {
  if (grade.verdict === "forces_mate" || grade.attemptedSan.includes("#")) return "checkmate";
  if (grade.deltaCp == null) return null;
  const loss = Math.max(0, -grade.deltaCp);
  const exactBest = grade.bestMoveSan != null && grade.attemptedSan === grade.bestMoveSan;
  if (
    exactBest &&
    grade.startingEvaluation?.cp != null &&
    grade.startingEvaluation.cp < -50 &&
    grade.evaluation?.cp != null &&
    grade.evaluation.cp > grade.startingEvaluation.cp + 100
  )
    return "brilliant";
  if (exactBest && loss === 0) return "best";
  if (loss <= 9) return "excellent";
  if (loss < 50) return "good";
  if (loss < 100) return "imprecision";
  if (loss < 200) return "mistake";
  return "blunder";
};

export const temporaryMoveEvidence = (
  beforeFen: string,
  move: AppliedMove | null,
  grade: TemporaryMoveGrade | null,
  book = false,
): TemporaryMoveEvidence | null => {
  if (move && book) return { destination: move.uci.slice(2, 4), motifs: ["book"], principleSignals: [] };
  if (!move || grade?.status !== "ready") return null;
  const motif = qualityMotif(grade);
  const centipawnLoss = grade.deltaCp == null ? 0 : Math.max(0, -grade.deltaCp);
  return {
    destination: move.uci.slice(2, 4),
    motifs: motif ? [motif] : [],
    principleSignals: principleSignalsForVerifiedMove(beforeFen, move.san, grade.bestMoveSan ?? null, centipawnLoss),
  };
};
